import React from 'react';
import { Helmet } from "react-helmet-async";
import { Button } from '@/components/ui/button';
import { ExternalLink, Lock, DollarSign, Clock, Shield, CheckCircle } from 'lucide-react';

export function SmartContractLocking() {
  return (
    <article className="prose prose-invert max-w-none">
      <Helmet>
        <title>Smart Contract Locking Using MDBApp.io - Complete Guide | MDBApp</title>
        <meta name="description" content="Learn how to lock your smart contract and renounce ownership securely using MDBApp.io. Save up to 99% compared to traditional developer costs. Fast, secure, and automated contract locking solution." />
        <meta name="keywords" content="smart contract locking, renounce ownership, contract security, MDBApp, blockchain security, DeFi security, contract ownership, BSC security" />
        <meta property="og:title" content="Smart Contract Locking Using MDBApp.io - Complete Guide" />
        <meta property="og:description" content="Secure your smart contract with automated locking and ownership renouncement. Save time and money with MDBApp's instant contract security solutions." />
        <meta name="twitter:title" content="Smart Contract Locking Using MDBApp.io - Complete Guide" />
        <meta name="twitter:description" content="Secure your smart contract instantly with MDBApp's automated locking solution. Save up to 99% on contract security." />
      </Helmet>

      <div className="space-y-8">
        <header className="space-y-4">
          <h1 className="text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-[#0a74ff] to-[#FFD700]">
            Simple Guide To Smart Contract Locking Using MDBApp.io
          </h1>
          
          <div className="flex items-center gap-2 text-muted-foreground">
            <div className="w-12 h-12 rounded-full overflow-hidden bg-gradient-to-r from-[#0a74ff] to-[#FFD700] p-[2px]">
              <div className="w-full h-full rounded-full overflow-hidden">
                <img 
                  src="/authors/dtdevmdbappio.jpg"
                  alt="Daniel T."
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.currentTarget.src = 'https://api.dicebear.com/7.x/initials/svg?seed=DT&backgroundColor=0a74ff';
                  }}
                />
              </div>
            </div>
            <div>
              <p className="font-medium">Daniel T. (MDB Dev)</p>
              <p className="text-sm">Published on December 27, 2023</p>
            </div>
          </div>
        </header>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Lock className="w-6 h-6 text-[#0a74ff]" />
            Understanding Smart Contract Locking
          </h2>
          <p>
            Smart contract locking and ownership renouncement are crucial steps in establishing trust and security in your blockchain project. At MDBApp.io, we've automated this process to make it accessible, affordable, and instant.
          </p>
          <div className="my-8 p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-[#0a74ff]/30">
            <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Traditional Cost Breakdown
            </h3>
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-red-500">$</span>
                </div>
                <div>
                  <strong className="text-red-500">Smart Contract Locking:</strong> $1,000 - $5,000
                  <p className="text-sm text-muted-foreground">Varies based on contract complexity and security requirements</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-1">
                  <span className="text-red-500">$</span>
                </div>
                <div>
                  <strong className="text-red-500">Renouncing Ownership:</strong> $500 - $1,500
                  <p className="text-sm text-muted-foreground">Cost for modifying contract and transferring control</p>
                </div>
              </li>
            </ul>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Shield className="w-6 h-6 text-[#0a74ff]" />
            The MDBApp.io Advantage
          </h2>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-[#0a74ff]/30">
              <Clock className="w-8 h-8 text-[#FFD700] mb-4" />
              <h3 className="text-xl font-semibold mb-2">Instant Processing</h3>
              <p className="text-muted-foreground">
                Complete contract locking in seconds instead of waiting days or weeks for manual developer implementation.
              </p>
            </div>
            <div className="p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-[#0a74ff]/30">
              <DollarSign className="w-8 h-8 text-green-500 mb-4" />
              <h3 className="text-xl font-semibold mb-2">99% Cost Savings</h3>
              <p className="text-muted-foreground">
                Save thousands of dollars by using our automated smart contract locking solution.
              </p>
            </div>
          </div>
        </section>

        <section className="space-y-4">
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <CheckCircle className="w-6 h-6 text-[#0a74ff]" />
            How to Lock Your Smart Contract
          </h2>
          <ol className="space-y-4 list-decimal list-inside">
            <li className="text-muted-foreground">
              Visit our <a href="https://mdbapp.io/bscchain/smartcontractlocker" className="text-[#0a74ff] hover:underline">Smart Contract Locker</a>
            </li>
            <li className="text-muted-foreground">Connect your wallet</li>
            <li className="text-muted-foreground">Enter your contract address</li>
            <li className="text-muted-foreground">Choose the features you want to lock</li>
            <li className="text-muted-foreground">Confirm the transaction</li>
          </ol>
          
          <div className="mt-8">
            <Button
              className="bg-[#0a74ff] hover:bg-[#0a74ff]/90"
              onClick={() => window.location.href = 'https://mdbapp.io/bscchain/smartcontractlocker'}
            >
              <Lock className="w-4 h-4 mr-2" />
              Lock Your Contract Now
              <ExternalLink className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        <section className="mt-12 p-6 bg-black/30 backdrop-blur-sm rounded-lg border border-[#0a74ff]/30">
          <h2 className="text-2xl font-semibold mb-4">Why Choose MDBApp.io?</h2>
          <ul className="space-y-4">
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Automated and instant processing</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Save up to 99% compared to traditional methods</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>User-friendly interface</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>Secure and audited process</span>
            </li>
            <li className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span>24/7 availability</span>
            </li>
          </ul>
        </section>
      </div>
    </article>
  );
}

export default SmartContractLocking;
